/*
 * Portions are Copyright (c) Sindre Sorhus <sindresorhus@gmail.com> (sindresorhus.com)
 *  https://github.com/Marak/colors.js
 */

const STYLE_CODES = {
    reset: [0, 0],

    bold: [1, 22],
    dim: [2, 22],
    italic: [3, 23],
    underline: [4, 24],
    inverse: [7, 27],
    hidden: [8, 28],
    strikethrough: [9, 29],

    black: [30, 39],
    red: [31, 39],
    green: [32, 39],
    yellow: [33, 39],
    blue: [34, 39],
    magenta: [35, 39],
    cyan: [36, 39],
    white: [37, 39],
    gray: [90, 39],
    grey: [90, 39],

    bgBlack: [40, 49],
    bgRed: [41, 49],
    bgGreen: [42, 49],
    bgYellow: [43, 49],
    bgBlue: [44, 49],
    bgMagenta: [45, 49],
    bgCyan: [46, 49],
    bgWhite: [47, 49]
};

export type BoundStyler = (...contents: (StyledString | string | number)[]) => StyledString;
export type StyleShortcuts = {
    [P in keyof typeof STYLE_CODES as `$${P}`]: (...contents: (StyledString | string | number)[]) => StyledString;
}

export type Styler = BoundStyler & StyleShortcuts;

export type Style = keyof typeof STYLE_CODES;
export const startStyle = (key: keyof typeof STYLE_CODES) => styleIndicator(key, 0);
export const endStyle = (key: keyof typeof STYLE_CODES) => styleIndicator(key, 1);

export function styleIndicator(key: keyof typeof STYLE_CODES, index: number) {
    if (key === undefined)
        return '';
    if (!(key in STYLE_CODES))
        throw new Error(`No such style '${key}'`);
    return `\u001b[${STYLE_CODES[key]?.[index]}m`;
}

export class StyledString {
    constructor(
        readonly style?: keyof typeof STYLE_CODES,
        readonly parts: (StyledString | string | number)[] = []
    ) {
    }

    start() {
        return startStyle(this.style);
    }

    end() {
        return endStyle(this.style);
    }

    toString() {
        return `${this.start()}${this.parts.join('')}${this.end()}`;
    }
}

let _style = (style: Style, ...contents: (string | number | StyledString)[]): Readonly<StyledString> => {
    return new StyledString(style, contents);
}

for (let key of <Style[]>Object.keys(STYLE_CODES))
    _style[`$${key}`] = (...contents: (string | number | StyledString)[]) => new StyledString(key, contents);

export const style = <Styler>_style;

export function styled(...contents: (string | number | StyledString)[]): string {
    return style(undefined, ...contents).toString();
}